import { AlertTriangle, Banknote, CheckCircle2, CheckSquare, Globe, Hourglass, KeyRound, Loader2, Send, ShieldCheck, XCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  executeBatch,
  fetchLivePayoutRates,
  listCompletedWithdrawals,
  listPendingWithdrawalIds,
  listPendingWithdrawals,
  listProcessingWithdrawals,
  listRecentPayoutBatches,
  markWithdrawalCompleted,
  rejectWithdrawal,
} from '@/api/admin';
import AdminDateRange, { dateRangeParams, EMPTY_DATE_RANGE, useDebouncedValue } from '@/components/admin/AdminDateRange';
import AdminPagination from '@/components/admin/AdminPagination';
import PayoutTwoFactorModal from '@/components/admin/PayoutTwoFactorModal';
import { fmtUsd } from '@/lib/money';

const money = (v) => `$${Number(v ?? 0).toFixed(2)}`;

// Batch statuses shown in the Awaiting 2FA panel. `verifying` means a code was sent but NOWPayments'
// answer never arrived — it settles on the next refresh, so it gets no Enter Code button.
const AWAITING_2FA_STATUSES = new Set(['awaiting_2fa', 'verifying']);
const BATCHES_PAGE_SIZE = 25;

// Hardcoded import-time fallback ONLY. Used until the first
// /api/admin/rates/live response lands (typically <500ms after mount)
// so the calc matrix never renders NaN. Real rates come from the
// backend, which sources them from NOWPayments' /v1/estimate with a
// 60s cache. NEVER use this constant for live computations - always
// prefer the `liveRates` state below.
const RATE_BOOTSTRAP_FALLBACK = {
  LTC: 72.5,
  TRX: 0.135,
  DOGE: 0.085,
};

const PayoutsSubpage = () => {
  const [page, setPage] = useState(1);
  // The pending list's requested-date filter; "Check All — All Pages" walks the ids with the same filter.
  const [pendingDates, setPendingDates] = useState(EMPTY_DATE_RANGE);
  const appliedPendingDates = useDebouncedValue(pendingDates);
  const [data, setData] = useState({ items: [], page: 1, total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  // selection = { [withdrawal_id]: { asset, amount_usd } }
  const [selection, setSelection] = useState({});
  const [allPagesMode, setAllPagesMode] = useState(false);
  const [allTotals, setAllTotals] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  // 2FA modal state — opened when the backend returns requires_2fa=true.
  const [twoFAState, setTwoFAState] = useState({
    open: false,
    batchId: null,
    nowpaymentsBatchId: null,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await listPendingWithdrawals(page, dateRangeParams(appliedPendingDates));
      setData(d);
    } catch {
      toast.error('Failed to load pending withdrawals');
    } finally {
      setLoading(false);
    }
  }, [page, appliedPendingDates]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Completed Payouts list ────────────────────────────────────────────
  // Separate state + loader so the Pending table never affects Completed
  // rendering and vice-versa. Page reloads on its own pagination only.
  const [completedPage, setCompletedPage] = useState(1);
  const [completedDates, setCompletedDates] = useState(EMPTY_DATE_RANGE);
  const appliedCompletedDates = useDebouncedValue(completedDates);
  const [completedData, setCompletedData] = useState({
    items: [],
    page: 1,
    total: 0,
    total_pages: 1,
  });
  const [completedLoading, setCompletedLoading] = useState(true);
  const loadCompleted = useCallback(async () => {
    setCompletedLoading(true);
    try {
      const d = await listCompletedWithdrawals(completedPage, dateRangeParams(appliedCompletedDates));
      setCompletedData(d);
    } catch {
      toast.error('Failed to load completed payouts');
    } finally {
      setCompletedLoading(false);
    }
  }, [completedPage, appliedCompletedDates]);
  useEffect(() => {
    loadCompleted();
  }, [loadCompleted]);

  // ── Processing Payouts list (Patch v14) ───────────────────────────────
  // Withdrawals dispatched to NOWPayments but whose payout IPN has not
  // (yet) flipped them to completed. Independent state + loader so it
  // never blocks Pending or Completed rendering.
  const [processingPage, setProcessingPage] = useState(1);
  const [processingDates, setProcessingDates] = useState(EMPTY_DATE_RANGE);
  const appliedProcessingDates = useDebouncedValue(processingDates);
  const [processingData, setProcessingData] = useState({
    items: [],
    page: 1,
    total: 0,
    total_pages: 1,
  });
  const [processingLoading, setProcessingLoading] = useState(true);
  const loadProcessing = useCallback(async () => {
    setProcessingLoading(true);
    try {
      const d = await listProcessingWithdrawals(processingPage, dateRangeParams(appliedProcessingDates));
      setProcessingData(d);
    } catch {
      toast.error('Failed to load processing payouts');
    } finally {
      setProcessingLoading(false);
    }
  }, [processingPage, appliedProcessingDates]);
  useEffect(() => {
    loadProcessing();
  }, [loadProcessing]);

  // ── Batches awaiting the NOWPayments 2FA code (newest first, server-paged and filtered by status) ──
  const [batchesPage, setBatchesPage] = useState(1);
  const [batchesDates, setBatchesDates] = useState(EMPTY_DATE_RANGE);
  const appliedBatchesDates = useDebouncedValue(batchesDates);
  const [batchesData, setBatchesData] = useState({ items: [], page: 1, total: 0, total_pages: 1 });
  const [awaitingLoading, setAwaitingLoading] = useState(true);
  const loadAwaiting = useCallback(async () => {
    setAwaitingLoading(true);
    try {
      setBatchesData(await listRecentPayoutBatches(batchesPage, BATCHES_PAGE_SIZE, dateRangeParams(appliedBatchesDates), [...AWAITING_2FA_STATUSES]));
    } catch {
      toast.error('Failed to load payout batches awaiting 2FA');
    } finally {
      setAwaitingLoading(false);
    }
  }, [batchesPage, appliedBatchesDates]);
  useEffect(() => {
    loadAwaiting();
  }, [loadAwaiting]);

  // Modals for Reject (pending row) + Mark Completed (processing row).
  // null = closed, {row} = open with target row.
  const [rejectModal, setRejectModal] = useState(null); // { row, reason, submitting }
  const [completeModal, setCompleteModal] = useState(null); // { row, tx_hash, note, submitting }

  const doReject = async () => {
    if (!rejectModal || rejectModal.submitting) return;
    setRejectModal((m) => ({ ...m, submitting: true }));
    try {
      const r = await rejectWithdrawal(rejectModal.row.withdrawal_id, rejectModal.reason);
      toast.success(`Rejected — ${fmtUsd(r.refunded_amount_usd)} refunded to ${rejectModal.row.username || 'user'}`);
      setRejectModal(null);
      // Refresh both lists: pending shrinks, completed/processing unaffected
      load();
      // Also remove from selection if it was selected
      setSelection((s) => {
        const next = { ...s };
        delete next[rejectModal.row.withdrawal_id];
        return next;
      });
    } catch (err) {
      const det = err?.response?.data?.detail;
      const msg = typeof det === 'object' ? det?.message || det?.code : det || err.message;
      toast.error(`Reject failed: ${msg}`);
      setRejectModal((m) => (m ? { ...m, submitting: false } : null));
    }
  };

  const doComplete = async () => {
    if (!completeModal || completeModal.submitting) return;
    setCompleteModal((m) => ({ ...m, submitting: true }));
    try {
      await markWithdrawalCompleted(completeModal.row.withdrawal_id, {
        tx_hash: completeModal.tx_hash,
        note: completeModal.note,
      });
      toast.success(`Marked completed — moved to Completed Payouts list.`);
      setCompleteModal(null);
      // Refresh both processing (it should drop the row) and completed (it should gain the row).
      loadProcessing();
      loadCompleted();
    } catch (err) {
      const det = err?.response?.data?.detail;
      const msg = typeof det === 'object' ? det?.message || det?.code : det || err.message;
      toast.error(`Mark Completed failed: ${msg}`);
      setCompleteModal((m) => (m ? { ...m, submitting: false } : null));
    }
  };

  // ── Live USD-per-asset rate quotes ────────────────────────────────────
  // Polls /api/admin/rates/live every 60s so the Live Calc Matrix and
  // Execute-Batch math use the SAME rate the backend will use to compile
  // payouts to NOWPayments. Eliminates the previous stale $72.50/LTC etc.
  // hardcoded drift. The shape is { rates: { LTC: { usd, source, age_seconds }, ... } }
  // where ``source`` is "live" (from NOWPayments /v1/estimate) or
  // "fallback" (hardcoded const if NOWPayments was unreachable).
  const [liveRates, setLiveRates] = useState({
    LTC: { usd: RATE_BOOTSTRAP_FALLBACK.LTC, source: 'bootstrap', age_seconds: 0 },
    TRX: { usd: RATE_BOOTSTRAP_FALLBACK.TRX, source: 'bootstrap', age_seconds: 0 },
    DOGE: { usd: RATE_BOOTSTRAP_FALLBACK.DOGE, source: 'bootstrap', age_seconds: 0 },
  });
  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      try {
        const r = await fetchLivePayoutRates();
        if (!alive) return;
        const next = {};
        ['LTC', 'TRX', 'DOGE'].forEach((a) => {
          const row = r?.rates?.[a];
          if (row?.usd) {
            next[a] = {
              usd: Number(row.usd),
              source: row.source || 'live',
              age_seconds: Number(row.age_seconds || 0),
            };
          }
        });
        if (Object.keys(next).length > 0) {
          setLiveRates((prev) => ({ ...prev, ...next }));
        }
      } catch {
        // Silent: we keep the previous (stale) rates rather than wiping
        // them to bootstrap values. Operator-facing toast would be noisy
        // for a 60s background poll.
      }
    };
    refresh();
    const id = setInterval(refresh, 60_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  // When the user switches “Check All — All Pages” ON, fetch the global list.
  useEffect(() => {
    if (!allPagesMode) {
      setAllTotals(null);
      return;
    }
    (async () => {
      try {
        const all = await listPendingWithdrawalIds(dateRangeParams(appliedPendingDates));
        setAllTotals(all);
        const next = {};
        all.rows.forEach((row) => {
          next[row.withdrawal_id] = { asset: row.asset, amount_usd: row.amount_usd };
        });
        setSelection(next);
      } catch {
        toast.error('Failed to expand selection across all pages');
        setAllPagesMode(false);
      }
    })();
  }, [allPagesMode, appliedPendingDates]);

  // Visible (current page) selection toggle.
  const allOnPageChecked = useMemo(() => data.items.length > 0 && data.items.every((row) => Boolean(selection[row.withdrawal_id])), [data.items, selection]);

  const togglePageAll = (checked) => {
    setAllPagesMode(false);
    setSelection((prev) => {
      const next = { ...prev };
      if (checked) {
        data.items.forEach((row) => {
          next[row.withdrawal_id] = { asset: row.asset, amount_usd: row.amount_usd };
        });
      } else {
        data.items.forEach((row) => {
          delete next[row.withdrawal_id];
        });
      }
      return next;
    });
  };

  const toggleRow = (row, checked) => {
    setAllPagesMode(false);
    setSelection((prev) => {
      const next = { ...prev };
      if (checked) next[row.withdrawal_id] = { asset: row.asset, amount_usd: row.amount_usd };
      else delete next[row.withdrawal_id];
      return next;
    });
  };

  // Live multi-crypto calculation matrix.
  const totals = useMemo(() => {
    const buckets = { LTC: 0, TRX: 0, DOGE: 0 };
    let grandUsd = 0;
    Object.values(selection).forEach((entry) => {
      const usd = Number(entry.amount_usd || 0);
      if (!Number.isFinite(usd)) return;
      buckets[entry.asset] = (buckets[entry.asset] || 0) + usd;
      grandUsd += usd;
    });
    const out = { byAsset: {}, grandUsd, count: Object.keys(selection).length };
    ['LTC', 'TRX', 'DOGE'].forEach((a) => {
      const rate = Number(liveRates[a]?.usd) || 0;
      const usd = buckets[a] || 0;
      const assetAmount = rate > 0 ? usd / rate : 0;
      out.byAsset[a] = { usd, assetAmount };
    });
    return out;
  }, [selection, liveRates]);

  const execute = async () => {
    setExecuting(true);
    try {
      const ids = Object.keys(selection);
      const r = await executeBatch(ids, allPagesMode ? 'all-pages selection' : 'page selection');
      toast.success(`Batch compiled (${r.selected_count} withdrawals, ${money(r.total_usd)} USD escrow)`);
      setSelection({});
      setAllPagesMode(false);
      setShowConfirm(false);
      const sub = r?.submission || {};
      // NOWPayments holds a submitted batch until its 2FA code is entered — ask for it right away.
      if (sub?.submitted && sub?.requires_2fa) {
        toast.success('Batch submitted to NOWPayments \u2014 enter the 2FA code to release the funds.', { duration: 8000 });
        setTwoFAState({ open: true, batchId: r.batch_id, nowpaymentsBatchId: sub.nowpayments_batch_id ?? null });
      } else if (sub && sub.submitted === false && sub.error) {
        // Stage-1: surface the full NOWPayments diagnostic surface (status
        // code + body when available) so the operator can read the exact
        // reason without DevTools. Persists for 9 seconds so it doesn't
        // auto-dismiss before they screenshot it.
        const det = sub.detail || {};
        const code = det.status_code ? ` [HTTP ${det.status_code}]` : '';
        let bodyMsg = '';
        try {
          if (det.body && typeof det.body === 'string') {
            const parsed = JSON.parse(det.body);
            bodyMsg = parsed?.message || parsed?.errorMessage || parsed?.code || '';
          } else if (det.body && typeof det.body === 'object') {
            bodyMsg = det.body?.message || det.body?.errorMessage || det.body?.code || '';
          }
        } catch (_e) {
          bodyMsg = '';
        }
        const detailLine = bodyMsg ? ` — ${bodyMsg}` : '';
        toast.error(`NOWPayments submission failed${code}: ${sub.error}${detailLine}`, {
          duration: 9000,
        });
      }
      load();
      loadCompleted();
      loadAwaiting();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Execute failed: ${msg}`);
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="payouts-subpage">
      <div className="sc-glass rounded-2xl p-5 flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div>
          <div className="font-display font-bold text-xl flex items-center gap-2">
            <Banknote className="w-5 h-5 text-[#FFC20E]" />
            NOWPayments Mass Payout Control Center
          </div>
          <p className="text-xs text-[var(--sc-text-dim)] mt-1 max-w-3xl">
            Every pending member withdrawal awaiting network confirmation. Use the dual-checkbox architecture below: <strong>“Check All — All Pages”</strong>{' '}
            flags every pending row in the database (across all paginated pages); <strong>“Check Page”</strong> selects only the 50 visible rows on this page.
          </p>
        </div>
        <label
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition ${
            allPagesMode
              ? 'bg-[rgba(255,182,18,0.10)] border-[rgba(255,182,18,0.45)] text-[#FFC20E]'
              : 'bg-[rgba(255,182,18,0.04)] border-[rgba(255,182,18,0.28)] text-[var(--sc-text-dim)] hover:border-[rgba(255,182,18,0.45)]'
          }`}
          data-testid="check-all-pages-wrapper"
        >
          <input
            type="checkbox"
            className="sc-checkbox"
            checked={allPagesMode}
            onChange={(e) => setAllPagesMode(e.target.checked)}
            data-testid="check-all-pages"
          />
          <Globe className="w-3.5 h-3.5" />
          <span className="text-[11px] uppercase tracking-[0.18em] font-semibold whitespace-nowrap">Check All — All Pages</span>
          {allPagesMode && allTotals ? <span className="text-[10px] text-[#FFE08A] sc-tabular">({allTotals.total})</span> : null}
        </label>
      </div>

      <div className="sc-glass rounded-2xl p-5">
        <div className="mb-3 flex justify-end">
          <AdminDateRange
            wrap
            dateFrom={pendingDates.date_from}
            dateTo={pendingDates.date_to}
            onChange={(range) => {
              setPendingDates(range);
              setPage(1);
            }}
            fromLabel="Requested From"
            toLabel="Requested To"
            testIdPrefix="payouts-pending-date"
          />
        </div>
        <div className="sc-admin-table-wrap">
          <table className="sc-admin-table" data-testid="payouts-table">
            <thead>
              <tr>
                <th style={{ width: 44 }}>
                  <input
                    type="checkbox"
                    className="sc-checkbox"
                    checked={allOnPageChecked}
                    onChange={(e) => togglePageAll(e.target.checked)}
                    data-testid="check-page"
                    title="Check this page only (50 rows)"
                  />
                </th>
                <th>Member</th>
                <th>Asset</th>
                <th className="text-right">Amount</th>
                <th>Destination</th>
                <th>Submitted</th>
                <th className="text-right" style={{ width: 90 }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && data.items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <Loader2 className="w-5 h-5 mx-auto animate-spin text-[#FFB612]" />
                  </td>
                </tr>
              ) : data.items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-[var(--sc-text-mute)]" data-testid="payouts-empty">
                    No pending withdrawals in the queue.
                  </td>
                </tr>
              ) : (
                data.items.map((row) => {
                  const checked = Boolean(selection[row.withdrawal_id]);
                  return (
                    <tr key={row.withdrawal_id} data-testid={`payout-row-${row.withdrawal_id}`}>
                      <td>
                        <input
                          type="checkbox"
                          className="sc-checkbox"
                          checked={checked}
                          onChange={(e) => toggleRow(row, e.target.checked)}
                          data-testid={`row-check-${row.withdrawal_id}`}
                        />
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm">{row.username || row.user_id}</span>
                          {row.is_admin_payout ? (
                            <span className="text-[9px] uppercase tracking-[0.2em] text-[#FFC20E] border border-[rgba(255,182,18,0.5)] px-1 py-0.5 rounded">
                              ADMIN
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#FFC20E]">{row.asset}</span>
                      </td>
                      <td className="text-right sc-tabular text-[#FFC20E]">{money(row.amount_usd)}</td>
                      <td className="text-xs text-[var(--sc-text-dim)] truncate max-w-[260px]">{row.destination_address}</td>
                      <td className="text-xs text-[var(--sc-text-mute)]">{String(row.created_at).slice(0, 19).replace('T', ' ')}</td>
                      <td className="text-right">
                        <button
                          type="button"
                          onClick={() => setRejectModal({ row, reason: '', submitting: false })}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[rgba(220,80,80,0.45)] text-[10px] uppercase tracking-[0.16em] font-bold text-[#FF9B9B] hover:bg-[rgba(220,80,80,0.10)] transition"
                          data-testid={`reject-pending-btn-${row.withdrawal_id}`}
                          title="Cancel this pending withdrawal and refund the user's withdrawal balance"
                        >
                          <XCircle className="w-3 h-3" />
                          Reject
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <AdminPagination page={data.page} totalPages={data.total_pages} total={data.total} onChange={(p) => setPage(p)} />
      </div>

      {/* Live Multi-Crypto Calculation Matrix */}
      <div className="sc-glass-strong rounded-2xl p-5" data-testid="payouts-calc">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#FFC20E]">Live Multi-Cryptocurrency Calculation Matrix</div>
            <div className="text-xs text-[var(--sc-text-dim)] mt-1">Totals recompute instantly as you select / deselect rows.</div>
          </div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">
            <span data-testid="selected-count">{totals.count}</span> selected
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          {['LTC', 'TRX', 'DOGE'].map((a) => {
            const v = totals.byAsset[a];
            const rateInfo = liveRates[a] || {};
            const rateNum = Number(rateInfo.usd) || 0;
            // Format rate with sensible precision per asset class so
            // sub-dollar assets (TRX/DOGE) don't get rounded to $0.00.
            const rateLabel = rateNum >= 1 ? rateNum.toFixed(2) : rateNum.toFixed(4);
            const isLive = rateInfo.source === 'live';
            const sourceTag = isLive ? 'live' : rateInfo.source === 'fallback' ? 'fallback' : 'loading';
            return (
              <div key={a} className="rounded-xl border border-[rgba(255,182,18,0.18)] bg-[rgba(0,0,0,0.42)] p-4" data-testid={`calc-${a}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Total {a}</span>
                  <span
                    className="text-[10px] text-[var(--sc-text-mute)]"
                    data-testid={`calc-${a}-rate`}
                    title={`Source: ${sourceTag}${rateInfo.age_seconds != null ? `  ·  refreshed ${Math.round(rateInfo.age_seconds)}s ago` : ''}`}
                  >
                    rate ${rateLabel} / {a}
                    {!isLive && rateNum > 0 ? ' *' : ''}
                  </span>
                </div>
                <div className="sc-tabular text-2xl font-display font-bold text-[#FFE08A] mt-1">
                  {Number(v?.assetAmount || 0).toFixed(6)} {a}
                </div>
                <div className="text-[12px] text-[var(--sc-text-dim)] mt-1">{money(v?.usd)} USD</div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 px-4 py-4 rounded-xl border border-[rgba(255,182,18,0.45)] bg-[rgba(255,182,18,0.06)] flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#FFC20E]">Total Mass Payout Escrow Amount</div>
            <div
              className="sc-tabular font-display font-black text-3xl md:text-4xl mt-1"
              style={{ color: '#FFE08A', letterSpacing: '-0.01em' }}
              data-testid="calc-grand-total"
            >
              {money(totals.grandUsd)} USD
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowConfirm(true)}
            disabled={totals.count === 0 || executing}
            className="sc-btn-gold"
            style={{ padding: '14px 22px', fontSize: 13 }}
            data-testid="execute-batch-btn"
          >
            {executing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Execute Mass Payout via NOWPayments
          </button>
        </div>
      </div>

      {showConfirm && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="execute-confirm">
          <button
            type="button"
            aria-label="Close"
            tabIndex={-1}
            className="absolute inset-0 bg-black/75 backdrop-blur-md"
            onClick={() => !executing && setShowConfirm(false)}
          />
          <div className="relative sc-glass-strong rounded-2xl w-full max-w-lg p-6">
            <div className="flex items-center gap-2 text-[#FFC20E]">
              <AlertTriangle className="w-5 h-5" />
              <div className="font-display font-bold text-lg">Confirm batch execution</div>
            </div>
            <p className="text-sm text-[var(--sc-text-dim)] mt-2">
              About to compile <strong className="text-[#FFC20E]">{totals.count}</strong> pending withdrawals into a single NOWPayments-ready batch payload.
            </p>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center text-[12px]">
              {['LTC', 'TRX', 'DOGE'].map((a) => (
                <div key={a} className="rounded-lg border border-[rgba(255,182,18,0.18)] py-2">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)]">{a}</div>
                  <div className="text-[#FFC20E] font-semibold sc-tabular">{Number(totals.byAsset[a]?.assetAmount || 0).toFixed(6)}</div>
                  <div className="text-[var(--sc-text-dim)] text-[10px]">{money(totals.byAsset[a]?.usd)}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 rounded-lg border border-[rgba(255,182,18,0.32)] bg-[rgba(255,182,18,0.06)] p-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#FFC20E]">Escrow total</div>
              <div className="sc-tabular text-2xl font-display font-bold text-[#FFE08A]">{money(totals.grandUsd)} USD</div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                disabled={executing}
                className="sc-btn-ghost"
                style={{ padding: '10px 14px', fontSize: 12 }}
                data-testid="execute-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={execute}
                disabled={executing}
                className="sc-btn-gold"
                style={{ padding: '10px 18px', fontSize: 12 }}
                data-testid="execute-confirm-btn"
              >
                {executing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                Confirm &amp; Compile Batch
              </button>
            </div>
          </div>
        </div>
      )}
      <AwaitingTwoFaSection
        loading={awaitingLoading}
        data={batchesData}
        page={batchesPage}
        onPageChange={setBatchesPage}
        dates={batchesDates}
        onDatesChange={(range) => {
          setBatchesDates(range);
          setBatchesPage(1);
        }}
        onRefresh={loadAwaiting}
        onEnterCode={(b) => setTwoFAState({ open: true, batchId: b.batch_id, nowpaymentsBatchId: b.nowpayments_batch_id })}
      />
      <ProcessingPayoutsSection
        loading={processingLoading}
        data={processingData}
        page={processingPage}
        onPageChange={setProcessingPage}
        dates={processingDates}
        onDatesChange={(range) => {
          setProcessingDates(range);
          setProcessingPage(1);
        }}
        onRefresh={loadProcessing}
        onMarkCompleted={(row) => setCompleteModal({ row, tx_hash: '', note: '', submitting: false })}
      />
      <CompletedPayoutsSection
        loading={completedLoading}
        data={completedData}
        page={completedPage}
        onPageChange={setCompletedPage}
        dates={completedDates}
        onDatesChange={(range) => {
          setCompletedDates(range);
          setCompletedPage(1);
        }}
        onRefresh={loadCompleted}
      />
      <PayoutTwoFactorModal
        open={twoFAState.open}
        batchId={twoFAState.batchId}
        nowpaymentsBatchId={twoFAState.nowpaymentsBatchId}
        onClose={() => setTwoFAState({ open: false, batchId: null, nowpaymentsBatchId: null })}
        onVerified={() => {
          setTwoFAState({ open: false, batchId: null, nowpaymentsBatchId: null });
          load();
          loadAwaiting();
          loadProcessing();
        }}
      />
      {rejectModal ? (
        <RejectWithdrawalModal
          row={rejectModal.row}
          reason={rejectModal.reason}
          submitting={rejectModal.submitting}
          onReasonChange={(v) => setRejectModal((m) => ({ ...m, reason: v }))}
          onCancel={() => setRejectModal(null)}
          onConfirm={doReject}
        />
      ) : null}
      {completeModal ? (
        <MarkCompletedModal
          row={completeModal.row}
          tx_hash={completeModal.tx_hash}
          note={completeModal.note}
          submitting={completeModal.submitting}
          onTxHashChange={(v) => setCompleteModal((m) => ({ ...m, tx_hash: v }))}
          onNoteChange={(v) => setCompleteModal((m) => ({ ...m, note: v }))}
          onCancel={() => setCompleteModal(null)}
          onConfirm={doComplete}
        />
      ) : null}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
//  Completed Payouts section
//  Stateless wrt the rest of the page so it renders regardless of pending
//  load state. Always visible \u2014 shows an explicit empty state when there
//  are no completed rows yet.
// ────────────────────────────────────────────────────────────────────────
const CompletedPayoutsSection = ({ loading, data, page, onPageChange, dates, onDatesChange, onRefresh }) => {
  const rows = data?.items || [];
  const totalPages = data?.total_pages || 1;
  return (
    <div className="sc-glass rounded-2xl p-5" data-testid="completed-payouts-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-[#FFC20E]">
          <CheckCircle2 className="w-4 h-4" />
          <div className="font-display font-semibold">Completed Payouts</div>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">{data?.total ?? 0} total</span>
        </div>
        <button
          type="button"
          className="sc-btn-ghost"
          style={{ padding: '6px 12px', fontSize: 11 }}
          onClick={onRefresh}
          disabled={loading}
          data-testid="completed-payouts-refresh"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Refresh'}
        </button>
      </div>
      <p className="text-[11px] text-[var(--sc-text-dim)] mt-1">
        Withdrawals that have settled on-chain (status =&nbsp;
        <span className="sc-tabular text-[#7CC9FF]">completed</span>
        ). Newest first, 50 per page. Populated automatically by the NOWPayments payout IPN once a batch finishes.
      </p>
      <div className="mt-3 flex justify-end">
        <AdminDateRange
          wrap
          dateFrom={dates.date_from}
          dateTo={dates.date_to}
          onChange={onDatesChange}
          fromLabel="Completed From"
          toLabel="Completed To"
          testIdPrefix="completed-payouts-date"
        />
      </div>

      {loading && rows.length === 0 ? (
        <div className="flex items-center gap-2 mt-4 text-[var(--sc-text-mute)] text-xs">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading completed payouts...
        </div>
      ) : rows.length === 0 ? (
        <div
          className="mt-4 rounded-xl border border-dashed border-[var(--sc-border)] px-4 py-8 text-center text-[var(--sc-text-mute)] text-xs"
          data-testid="completed-payouts-empty"
        >
          No completed payouts yet. They&apos;ll appear here automatically once a batch finishes settling on NOWPayments.
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-xs" data-testid="completed-payouts-table">
            <thead className="text-left text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">
              <tr>
                <th className="py-2 pr-3">User</th>
                <th className="py-2 pr-3">Asset</th>
                <th className="py-2 pr-3 text-right">USD</th>
                <th className="py-2 pr-3 text-right">Crypto Amount</th>
                <th className="py-2 pr-3">Destination</th>
                <th className="py-2 pr-3">Settled At</th>
                <th className="py-2 pr-3">Tx Hash</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.withdrawal_id} className="border-t border-[var(--sc-border)]/40" data-testid={`completed-payout-row-${r.withdrawal_id}`}>
                  <td className="py-2 pr-3 text-[var(--sc-text-fg)]">
                    {r.username || r.user_id?.slice(0, 8)}
                    {r.is_admin_payout ? <span className="ml-1 text-[9px] uppercase tracking-[0.15em] text-[#FFE08A]">admin</span> : null}
                  </td>
                  <td className="py-2 pr-3 text-[var(--sc-text-fg)]">{r.asset}</td>
                  <td className="py-2 pr-3 text-right sc-tabular text-[#FFE08A]">{money(r.amount_usd)}</td>
                  <td className="py-2 pr-3 text-right sc-tabular text-[var(--sc-text-dim)]">{r.asset_amount ?? '\u2014'}</td>
                  <td className="py-2 pr-3 sc-tabular text-[var(--sc-text-dim)]" title={r.destination_address}>
                    {r.destination_address ? `${r.destination_address.slice(0, 8)}\u2026${r.destination_address.slice(-6)}` : '\u2014'}
                  </td>
                  <td className="py-2 pr-3 sc-tabular text-[var(--sc-text-dim)]">
                    {r.processed_at ? r.processed_at.slice(0, 19).replace('T', ' ') : '\u2014'}
                  </td>
                  <td className="py-2 pr-3 sc-tabular text-[#7CC9FF]" title={r.tx_hash}>
                    {r.tx_hash ? `${r.tx_hash.slice(0, 8)}\u2026${r.tx_hash.slice(-6)}` : '\u2014'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 0 ? (
        <div className="mt-3">
          <AdminPagination page={page} totalPages={totalPages} total={data?.total} onChange={onPageChange} data-testid="completed-payouts-pagination" />
        </div>
      ) : null}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
//  Processing Payouts section (Patch v14)
//  Lists withdrawals that have been dispatched to NOWPayments via the
//  Execute Batch flow but whose payout IPN has not (yet) flipped them
//  to "completed". Without this section those rows are invisible —
//  they leave the Pending list when admin clicks Execute, but never
//  appear in Completed until the IPN fires.
//
//  Each row carries a "Mark Completed" action so admins can manually
//  finalize a row once they've verified on-chain that the user got
//  paid (NOWPayments dashboard, block explorer, etc).
// ────────────────────────────────────────────────────────────────────────
const AwaitingTwoFaSection = ({ loading, data, page, onPageChange, dates, onDatesChange, onRefresh, onEnterCode }) => {
  const batches = data?.items || [];
  return (
    <div className="sc-glass rounded-2xl p-5" data-testid="awaiting-2fa-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-[#FFC20E]">
          <ShieldCheck className="w-4 h-4" />
          <div className="font-display font-semibold">Awaiting 2FA</div>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">{data?.total ?? 0} total</span>
        </div>
        <button
          type="button"
          className="sc-btn-ghost"
          style={{ padding: '6px 12px', fontSize: 11 }}
          onClick={onRefresh}
          disabled={loading}
          data-testid="awaiting-2fa-refresh"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Refresh'}
        </button>
      </div>
      <p className="text-[11px] text-[var(--sc-text-dim)] mt-1">
        Batches submitted to NOWPayments that are waiting for the 2FA code. Enter the code from your authenticator app (or the NOWPayments email) to release the
        funds.
      </p>
      <div className="mt-3 flex justify-end">
        <AdminDateRange
          wrap
          dateFrom={dates.date_from}
          dateTo={dates.date_to}
          onChange={onDatesChange}
          fromLabel="Compiled From"
          toLabel="Compiled To"
          testIdPrefix="awaiting-2fa-date"
        />
      </div>

      {loading && batches.length === 0 ? (
        <div className="flex items-center gap-2 mt-4 text-[var(--sc-text-mute)] text-xs">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading batches...
        </div>
      ) : batches.length === 0 ? (
        <div
          className="mt-4 rounded-xl border border-dashed border-[var(--sc-border)] px-4 py-8 text-center text-[var(--sc-text-mute)] text-xs"
          data-testid="awaiting-2fa-empty"
        >
          No batches waiting for a 2FA code.
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-xs" data-testid="awaiting-2fa-table">
            <thead className="text-left text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">
              <tr>
                <th className="py-2 pr-3">Batch</th>
                <th className="py-2 pr-3">NOWPayments Id</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3 text-right">Payouts</th>
                <th className="py-2 pr-3 text-right">USD</th>
                <th className="py-2 pr-3">Submitted</th>
                <th className="py-2 pr-3 text-right" style={{ width: 150 }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.batch_id} className="border-t border-[var(--sc-border)]/40" data-testid={`awaiting-2fa-row-${b.batch_id}`}>
                  <td className="py-2 pr-3 sc-tabular text-[var(--sc-text-fg)]">{b.batch_id}</td>
                  <td className="py-2 pr-3 sc-tabular text-[var(--sc-text-dim)]">{b.nowpayments_batch_id ?? '\u2014'}</td>
                  <td className="py-2 pr-3 sc-tabular text-[var(--sc-text-dim)]">{b.status}</td>
                  <td className="py-2 pr-3 text-right sc-tabular text-[var(--sc-text-fg)]">{b.selected_count}</td>
                  <td className="py-2 pr-3 text-right sc-tabular text-[#FFE08A]">{money(b.total_usd)}</td>
                  <td className="py-2 pr-3 sc-tabular text-[var(--sc-text-dim)]">{b.created_at ? b.created_at.slice(0, 19).replace('T', ' ') : '\u2014'}</td>
                  <td className="py-2 pr-3 text-right">
                    {b.status === 'awaiting_2fa' ? (
                      <button
                        type="button"
                        onClick={() => onEnterCode(b)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-[rgba(255,182,18,0.55)] text-[10px] uppercase tracking-[0.16em] font-bold text-[#FFC20E] hover:bg-[rgba(255,182,18,0.10)] transition"
                        data-testid={`enter-2fa-btn-${b.batch_id}`}
                      >
                        <KeyRound className="w-3 h-3" />
                        Enter 2FA Code
                      </button>
                    ) : b.status === 'verifying' ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] text-[var(--sc-text-mute)]">
                        <Loader2 className="w-3 h-3 animate-spin" /> Verifying
                      </span>
                    ) : (
                      <span className="text-[var(--sc-text-mute)]">{'\u2014'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {batches.length > 0 ? (
        <div className="mt-3">
          <AdminPagination
            page={page}
            totalPages={data?.total_pages || 1}
            total={data?.total}
            pageSize={data?.page_size || BATCHES_PAGE_SIZE}
            onChange={onPageChange}
          />
        </div>
      ) : null}
    </div>
  );
};

const ProcessingPayoutsSection = ({ loading, data, page, onPageChange, dates, onDatesChange, onRefresh, onMarkCompleted }) => {
  const rows = data?.items || [];
  const totalPages = data?.total_pages || 1;
  return (
    <div className="sc-glass rounded-2xl p-5" data-testid="processing-payouts-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-[#FFC20E]">
          <Hourglass className="w-4 h-4" />
          <div className="font-display font-semibold">Processing Payouts</div>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">{data?.total ?? 0} total</span>
        </div>
        <button
          type="button"
          className="sc-btn-ghost"
          style={{ padding: '6px 12px', fontSize: 11 }}
          onClick={onRefresh}
          disabled={loading}
          data-testid="processing-payouts-refresh"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Refresh'}
        </button>
      </div>
      <p className="text-[11px] text-[var(--sc-text-dim)] mt-1">
        Withdrawals dispatched to NOWPayments and awaiting on-chain settlement (status =&nbsp;
        <span className="sc-tabular text-[#FFE08A]">processing</span>
        ). These auto-flip to <em>completed</em> when the NOWPayments payout IPN arrives. If a row is stuck here long after the on-chain transaction confirmed,
        use the
        <strong> Mark Completed</strong> button to manually finalize it.
      </p>
      <div className="mt-3 flex justify-end">
        <AdminDateRange
          wrap
          dateFrom={dates.date_from}
          dateTo={dates.date_to}
          onChange={onDatesChange}
          fromLabel="Requested From"
          toLabel="Requested To"
          testIdPrefix="processing-payouts-date"
        />
      </div>

      {loading && rows.length === 0 ? (
        <div className="flex items-center gap-2 mt-4 text-[var(--sc-text-mute)] text-xs">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading processing payouts...
        </div>
      ) : rows.length === 0 ? (
        <div
          className="mt-4 rounded-xl border border-dashed border-[var(--sc-border)] px-4 py-8 text-center text-[var(--sc-text-mute)] text-xs"
          data-testid="processing-payouts-empty"
        >
          No payouts currently processing. Anything dispatched via Execute Batch will appear here until the NOWPayments payout IPN arrives.
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-xs" data-testid="processing-payouts-table">
            <thead className="text-left text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">
              <tr>
                <th className="py-2 pr-3">User</th>
                <th className="py-2 pr-3">Asset</th>
                <th className="py-2 pr-3 text-right">USD</th>
                <th className="py-2 pr-3 text-right">Crypto Amount</th>
                <th className="py-2 pr-3">Destination</th>
                <th className="py-2 pr-3">Dispatched</th>
                <th className="py-2 pr-3 text-right" style={{ width: 130 }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.withdrawal_id} className="border-t border-[var(--sc-border)]/40" data-testid={`processing-payout-row-${r.withdrawal_id}`}>
                  <td className="py-2 pr-3 text-[var(--sc-text-fg)]">
                    {r.username || r.user_id?.slice(0, 8)}
                    {r.is_admin_payout ? <span className="ml-1 text-[9px] uppercase tracking-[0.15em] text-[#FFE08A]">admin</span> : null}
                  </td>
                  <td className="py-2 pr-3 text-[var(--sc-text-fg)]">{r.asset}</td>
                  <td className="py-2 pr-3 text-right sc-tabular text-[#FFE08A]">{money(r.amount_usd)}</td>
                  <td className="py-2 pr-3 text-right sc-tabular text-[var(--sc-text-dim)]">{r.asset_amount ?? '\u2014'}</td>
                  <td className="py-2 pr-3 sc-tabular text-[var(--sc-text-dim)]" title={r.destination_address}>
                    {r.destination_address ? `${r.destination_address.slice(0, 8)}\u2026${r.destination_address.slice(-6)}` : '\u2014'}
                  </td>
                  <td className="py-2 pr-3 sc-tabular text-[var(--sc-text-dim)]">{r.created_at ? r.created_at.slice(0, 19).replace('T', ' ') : '\u2014'}</td>
                  <td className="py-2 pr-3 text-right">
                    <button
                      type="button"
                      onClick={() => onMarkCompleted(r)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-[rgba(94,230,161,0.45)] text-[10px] uppercase tracking-[0.16em] font-bold text-[#5EE6A1] hover:bg-[rgba(94,230,161,0.10)] transition"
                      data-testid={`mark-completed-btn-${r.withdrawal_id}`}
                      title="Manually finalize this processing withdrawal"
                    >
                      <CheckSquare className="w-3 h-3" />
                      Mark Completed
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 0 ? (
        <div className="mt-3">
          <AdminPagination page={page} totalPages={totalPages} total={data?.total} onChange={onPageChange} data-testid="processing-payouts-pagination" />
        </div>
      ) : null}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
//  Reject Withdrawal modal (Patch v14)
//  Destructive action — confirms before flipping status to rejected and
//  refunding the user's withdrawal_balance.
// ────────────────────────────────────────────────────────────────────────
const RejectWithdrawalModal = ({ row, reason, submitting, onReasonChange, onCancel, onConfirm }) => (
  <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="reject-withdrawal-modal">
    <button
      type="button"
      aria-label="Close"
      tabIndex={-1}
      className="absolute inset-0 bg-black/75 backdrop-blur-md"
      onClick={() => !submitting && onCancel()}
    />
    <div className="relative sc-glass-strong rounded-2xl w-full max-w-lg p-6">
      <div className="flex items-center gap-2 text-[#FF9B9B]">
        <XCircle className="w-5 h-5" />
        <div className="font-display font-bold text-lg">Reject pending withdrawal?</div>
      </div>
      <p className="text-sm text-[var(--sc-text-dim)] mt-2">
        About to reject{' '}
        <span className="text-[#FFC20E] font-semibold">
          ${Number(row?.amount_usd || 0).toFixed(2)} {row?.asset}
        </span>{' '}
        for <span className="text-[#FFE08A] font-semibold">{row?.username || row?.user_id?.slice(0, 8)}</span>.
      </p>
      <p className="text-xs text-[var(--sc-text-mute)] mt-2">
        Funds will be returned to the user&apos;s Withdrawal Balance and a ledger refund entry will be recorded. This action is logged with your admin id.
      </p>
      <div className="mt-4">
        <label htmlFor="reject-reason-input" className="block text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mb-1">
          Reason (optional, max 500 chars)
        </label>
        <textarea
          id="reject-reason-input"
          rows={3}
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          placeholder="e.g. user requested cancel, suspected wallet error, ..."
          className="w-full rounded-lg bg-[rgba(0,0,0,0.4)] border border-[var(--sc-border)] p-2 text-xs text-[var(--sc-text-fg)] focus:outline-none focus:border-[rgba(220,80,80,0.55)] resize-none"
          maxLength={500}
          data-testid="reject-reason-input"
        />
      </div>
      <div className="flex items-center justify-end gap-2 mt-5">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="sc-btn-ghost"
          style={{ padding: '10px 14px', fontSize: 12 }}
          data-testid="reject-cancel-btn"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={submitting}
          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-[rgba(220,80,80,0.55)] bg-[rgba(220,80,80,0.12)] text-[#FF9B9B] text-xs font-semibold uppercase tracking-[0.16em] hover:bg-[rgba(220,80,80,0.20)] transition disabled:opacity-60"
          data-testid="reject-confirm-btn"
        >
          {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
          {submitting ? 'Rejecting...' : 'Reject & Refund'}
        </button>
      </div>
    </div>
  </div>
);

// ────────────────────────────────────────────────────────────────────────
//  Mark Completed modal (Patch v14)
//  Manually finalize a stuck processing withdrawal. Optional tx_hash and
//  free-text note for audit trail.
// ────────────────────────────────────────────────────────────────────────
const MarkCompletedModal = ({ row, tx_hash, note, submitting, onTxHashChange, onNoteChange, onCancel, onConfirm }) => (
  <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="mark-completed-modal">
    <button
      type="button"
      aria-label="Close"
      tabIndex={-1}
      className="absolute inset-0 bg-black/75 backdrop-blur-md"
      onClick={() => !submitting && onCancel()}
    />
    <div className="relative sc-glass-strong rounded-2xl w-full max-w-lg p-6">
      <div className="flex items-center gap-2 text-[#5EE6A1]">
        <CheckSquare className="w-5 h-5" />
        <div className="font-display font-bold text-lg">Mark withdrawal as completed?</div>
      </div>
      <p className="text-sm text-[var(--sc-text-dim)] mt-2">
        About to manually finalize{' '}
        <span className="text-[#FFC20E] font-semibold">
          ${Number(row?.amount_usd || 0).toFixed(2)} {row?.asset}
        </span>{' '}
        for <span className="text-[#FFE08A] font-semibold">{row?.username || row?.user_id?.slice(0, 8)}</span>.
      </p>
      <p className="text-xs text-[var(--sc-text-mute)] mt-2">
        Only use this when you have <strong>verified on-chain</strong> (NOWPayments dashboard or block explorer) that the user actually received the payout. No
        balance change happens — only the status flips to <em>completed</em>.
      </p>
      <div className="mt-4">
        <label htmlFor="mark-completed-tx-input" className="block text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mb-1">
          Transaction hash (optional)
        </label>
        <input
          id="mark-completed-tx-input"
          type="text"
          value={tx_hash}
          onChange={(e) => onTxHashChange(e.target.value)}
          placeholder="0x... or chain-native hash"
          className="w-full rounded-lg bg-[rgba(0,0,0,0.4)] border border-[var(--sc-border)] p-2 text-xs sc-tabular text-[#7CC9FF] focus:outline-none focus:border-[rgba(94,230,161,0.55)]"
          maxLength={255}
          data-testid="mark-completed-tx-input"
        />
      </div>
      <div className="mt-3">
        <label htmlFor="mark-completed-note-input" className="block text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mb-1">
          Note (optional, max 500 chars)
        </label>
        <textarea
          id="mark-completed-note-input"
          rows={2}
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          placeholder="e.g. verified via NP dashboard, IPN never arrived after Cloudflare maintenance"
          className="w-full rounded-lg bg-[rgba(0,0,0,0.4)] border border-[var(--sc-border)] p-2 text-xs text-[var(--sc-text-fg)] focus:outline-none focus:border-[rgba(94,230,161,0.55)] resize-none"
          maxLength={500}
          data-testid="mark-completed-note-input"
        />
      </div>
      <div className="flex items-center justify-end gap-2 mt-5">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="sc-btn-ghost"
          style={{ padding: '10px 14px', fontSize: 12 }}
          data-testid="mark-completed-cancel-btn"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={submitting}
          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-[rgba(94,230,161,0.55)] bg-[rgba(94,230,161,0.12)] text-[#5EE6A1] text-xs font-semibold uppercase tracking-[0.16em] hover:bg-[rgba(94,230,161,0.20)] transition disabled:opacity-60"
          data-testid="mark-completed-confirm-btn"
        >
          {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          {submitting ? 'Marking...' : 'Mark Completed'}
        </button>
      </div>
    </div>
  </div>
);

export default PayoutsSubpage;
